
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ProductList } from './ProductList';
import { Sidebar } from './ui/Sidebar';
import { MobileFilters } from './ui/MobileFilters';
import { ViewToggle } from './ui/ViewToggle';
import { supabase } from '../lib/supabaseClient';
import { useParams, Link } from 'react-router-dom';
import { DollarSign } from 'lucide-react';

async function fetchCategoryMetadata(slug) {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .single();

  if (error) {
    console.warn(`Category fetch error: ${error.message}`);
    return null;
  }

  return data;
}

export function CategoryPage({ slug: propSlug, filter: propFilter }) {
  const { categorySlug, filter } = useParams();
  const currentSlug = propSlug || categorySlug || 'all';
  const currentFilter = propFilter || filter || '';

  const [filters, setFilters] = React.useState({
    search: currentFilter || '', // Use filter as search term
    category: currentSlug,
    productType: 'all',
    minPrice: '',
    maxPrice: '',
    stockStatus: '',
    sort: 'featured',
  });

  const [view, setView] = React.useState('list');

  const { data: metadata, isLoading, error } = useQuery({
    queryKey: ['categoryMetadata', currentSlug],
    queryFn: () => fetchCategoryMetadata(currentSlug),
    enabled: !!currentSlug && currentSlug.trim() !== '',
  });

  // Update filters when slug or filter changes
  React.useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      category: currentSlug,
      search: currentFilter || '', // Set search to filter value
    }));
  }, [currentSlug, currentFilter]);

  // Debug logs
  console.log('Requested category slug:', currentSlug);
  console.log('Applied filter:', currentFilter);
  console.log('Category metadata:', metadata);
  console.log('Current filters state:', filters);

  // Handle loading and error states
  if (isLoading) return <div className="p-8 text-center">Loading category...</div>;
  if (error || !metadata)
    return (
      <div className="max-w-xl mx-auto text-center mt-16">
        <h1 className="text-2xl font-bold text-red-600">Category Not Found</h1>
        <p className="mt-4 text-gray-600">
          The category <strong>{currentSlug}</strong> does not exist or is inactive.
        </p>
      </div>
    );

  const title = metadata?.name
    ? `${metadata.name}${currentFilter ? ` - ${currentFilter}` : ''} - Best Deals & Reviews ${new Date().getFullYear()}`
    : 'Popular Tech Categories - Reviews & Guides';

  const description =
    metadata?.description ||
    'Explore top-rated computer hardware, gadgets, and accessories. Get expert comparisons and deals.';

  const keywords =
    metadata?.meta_keywords?.join(', ') ||
    'tech, hardware, best monitors, gaming, reviews, computer accessories';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <header className="w-full" role="banner">
          <div className="max-w-7xl mx-auto px-4 pt-8">
            <div className="flex justify-between items-center bg-white dark:bg-slate-800 rounded-lg shadow-sm hover:shadow-md transition-shadow px-6 py-8">
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  {metadata?.name || 'All Products'} - Best Deals & Reviews {new Date().getFullYear()}
                  {currentFilter && <span className="text-blue-600 dark:text-blue-400 ml-2">({currentFilter})</span>}
                </h1>
                {metadata?.description && (
                  <p className="mt-2 text-gray-600 dark:text-gray-300">{metadata.description}</p>
                )}
                {currentFilter && (
                  <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                    Filtering by: {currentFilter}
                  </p>
                )}
              </div>
              <ViewToggle view={view} onViewChange={setView} />
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8" role="main">
          {/* SEO Internal Links - Price Filter Variants */}
          <div className="mb-6 bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <DollarSign className="w-4 h-4 mr-2" />
              Shop by Budget
            </h2>
            <div className="flex flex-wrap gap-2">
              <Link 
                to={`/category/${currentSlug}/filter?max=300`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                Under $300
              </Link>
              <Link 
                to={`/category/${currentSlug}/filter?min=300&max=600`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                $300 - $600
              </Link>
              <Link 
                to={`/category/${currentSlug}/filter?min=600&max=1000`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                $600 - $1000
              </Link>
              <Link 
                to={`/category/${currentSlug}/filter?min=1000`}
                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                Over $1000
              </Link>
            </div>
          </div>

          <div className="flex gap-8">
            <Sidebar filters={filters} onFilterChange={setFilters} />
            <div className="w-full flex-grow">
              <ProductList filters={filters} view={view} />
            </div>
          </div>
        </main>

        <MobileFilters filters={filters} onFilterChange={setFilters} />
      </div>
    </>
  );
}
